import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';

import { WORKSPACE_INVITE_LOGIN, WORKSPACE_URL_REGEX } from '../constants/routes';
import { VERIFY_WORKSPACE_INVITE } from '../apis/workspace';
import { INVITE_TOKEN, INVITE_WORKSPACE_NAME } from '../constants/common';

const WorkspaceInviteContext = createContext({});

const workspaceUrlRegex = new RegExp(WORKSPACE_URL_REGEX);

const isInvitePath = () => !workspaceUrlRegex.test(window.location.host) && window.location.pathname.includes(WORKSPACE_INVITE_LOGIN);

const getParamFromUrl = (key) => new URLSearchParams(window.location.search).get(key);

export const WorkspaceInviteProvider = ({ children }) => {
  // states
  const [isInviteUser, setIsInviteUser] = useState(isInvitePath);
  const [isInviteVerified, setIsInviteVerified] = useState();

  // constants
  const token = useMemo(() => getParamFromUrl(INVITE_TOKEN), [isInviteUser]);
  const workspaceName = useMemo(() => getParamFromUrl(INVITE_WORKSPACE_NAME), [isInviteUser]);
  const email = useMemo(() => getParamFromUrl('email'), [isInviteUser]);

  // mutations/queries
  const [verifyWorkspaceInvite, { loading: isInviteVerifying }] = useLazyQuery(VERIFY_WORKSPACE_INVITE, { fetchPolicy: 'network-only' });

  useEffect(() => {
    const verifyInvite = async () => {
      try {
        const variables = email ? { token, email } : { token };
        const { data, errors, error } = await verifyWorkspaceInvite({ variables });
        if ((errors || error) && !data.verifyWorkspaceInvite) setIsInviteVerified(false);
        else setIsInviteVerified(true);
      } catch (error) {
        setIsInviteVerified(false);
      }
    };
    if (isInviteUser) verifyInvite();
  }, [isInviteUser]);

  // constants
  const values = useMemo(() => ({
    token,
    email,
    isInviteUser,
    isInviteVerified,
    isInviteVerifying,
    workspaceName,
    setIsInviteVerified,
    setIsInviteUser,
  }), [token, isInviteUser, isInviteVerifying, isInviteVerified]);

  return <WorkspaceInviteContext.Provider value={values}>{children}</WorkspaceInviteContext.Provider>;
};

export const useWorkspaceInvite = () => {
  const context = useContext(WorkspaceInviteContext);
  if (context === undefined) {
    throw new Error('useWorkspaceInvite must be used within a WorkspaceInviteProvider');
  }
  return context;
};

WorkspaceInviteProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default useWorkspaceInvite;
